import Vue from 'vue';
import Vuex from 'vuex';
import moment from 'moment';
import shortid from 'shortid';

import bucket from './dashboard/bucket.js';
import folder from './dashboard/folder.js';
import note from './dashboard/note.js';
import settings from './dashboard/settings.js';
import user from './dashboard/user.js';
import ui from './dashboard/ui.js';
import cache from './dashboard/cache.js';
import realtimeio from './dashboard/realtimeio.js';
import message from './dashboard/message.js';
import dashboard from './dashboard/dashboard.js';
import smartCopy from './dashboard/smartcopy.js';
import event from './dashboard/event.js';

import http from '@/lib/http.js';
import checkId from '@/lib/check-id.js';
import folderAdapter from '@/lib/folder-adapter.js';
import storageManager from '@/lib/storage-manager.js';

import api from '@/lib/api.js';

Vue.use(Vuex);

function createId() {
	var timestamp = moment().valueOf();
	var uniqId = shortid.generate();
	return timestamp + '-' + uniqId;
}

let url = 'http://localhost:4000'; //"https://api.underlineme.com";

if (process.env.NODE_ENV === 'production') {
	url = 'https://api.underlineme.com';
}

window.url = url;

let store = new Vuex.Store({
	state: {
		init: false, // only becomes true when auth has been resolved
		loaded: false, // only becomes true when all data has been loaded initially. Subsequent network requests won't flip this state
		session_id: false,
		firstRun: true,
		afterSignup: false,
		pages: [
			{
				name: 'Main',
				route: '/'
			}
		],
		packageVersion: process.env.PACKAGE_VERSION || '0',
		theme: 'default',
		tabId: false,
		debounce: 400,
		mode: '',
		baseUrl: url,
		meta: {}
	},
	getters: {
		init: function(state) {
			return state.init;
		},
		loaded: function(state) {
			return state.loaded;
		},
		mode: function(state) {
			return state.mode;
		},
		afterSignup: function(state) {
			return state.afterSignup;
		},
		version: function(state) {
			return state.packageVersion;
		},
		production: function() {
			if (typeof webpackHotUpdate !== 'undefined') {
				return false;
			} else {
				return true;
			}
		},
		debounce: function(state) {
			return state.debounce;
		},
		theme: function(state) {
			return state.theme;
		},
		color: function(state) {
			return state.color;
		}
	},
	mutations: {
		SET_SETTINGS: function(state, settings) {
			state.settings = settings;
		},
		SET_META: function(state, meta) {
			state.meta = meta;
		},
		SET_MODE: function(state, mode) {
			state.mode = mode;
		},

		SET_FIRST_RUN: function(state) {
			state.firstRun = false;
		},
		SET_AFTER_SIGNUP: function(state) {
			state.afterSignup = true;
		},
		SAVE_ID: function(state, id) {
			state.id = id;
			saveProperty({
				id: id
			});
		},
		SET_ID: function(state, id) {
			state.id = id;
		},
		SET_TABID: function(state, tabId) {
			state.tabId = tabId;
		},
		SET_LOADED: function(state, action) {
			state.loaded = action;
		}
	},
	actions: {
		setSession: function(context) {
			return new Promise(resolve => {
				var session_id = localStorage.getItem('session_id');
				storageManager.get('session_id').then(data => {
					if (checkId(data.session_id)) {
						context.commit('SET_ID', session_id);
						context.commit('SET_FIRST_RUN');
					} else {
						let session_id = createId();
						storageManager.set({
							session_id: session_id
						});
					}
					http.setHeader('session_id', context.state.session_id);
					resolve();
				});
			});
		},
		init: function(context) {
			return new Promise((resolve, reject) => {
				if (chrome.permissions) {
					window.mode = 'EXTENSION';
					window.browser = require('webextension-polyfill');
				} else {
					window.mode = 'WEBAPP';
				}

				api.init();

				http.setContext(context);
				http.setUrl(context.state.baseUrl);
				context.dispatch('ui/setLoaderState', true);
				context.dispatch('ui/setColors');
				context.dispatch('cache/init');
				context.dispatch('user/init');
				context.dispatch('settings/getUiState');
				//context.dispatch('setupRuntimeEventListeners');
				context.dispatch('setSession').then(() => {
					var isAuth = context.getters['user/isAuth'];

					context.state.init = true;

					if (isAuth) {
						context.dispatch('afterLogin');
					} else {
						context.dispatch('ui/setLoaderState', false);
						context.commit('SET_LOADED', true);
					}

					resolve(isAuth);
				});
			});
		},
		async afterAuth(context) {
			await context.dispatch('afterLogin');
		},
		async afterLogin(context) {
			context.dispatch('ui/setListState');
			await context.dispatch('realtimeio/setupEventListeners');
			await context.dispatch('setData');
			context.dispatch('ui/fetchUpdates'); // no need for this to be in sync

			context.dispatch('ui/setLoaderState', false);
			context.commit('SET_LOADED', true);
			return true;
		},
		afterLogout: function(context) {
			context.dispatch('realtimeio/disconnect');
		},
		async setData(context) {
			await context.dispatch('setAuthorizationHeader');
			await context.dispatch('folder/getFolders');
			await context.dispatch('bucket/getBuckets');
			await context.dispatch('note/getNotes');
			return true;
		},
		async setAuthorizationHeader(context) {
			http.setHeader(
				'Authorization',
				`Bearer ${context.getters['user/token']}`
			);
			return true;
			a;
		},
		setMode: function(context, mode) {
			context.commit('SET_MODE', mode);
			context.commit('dashboard/TOGGLE_SIDEBAR', false);
		},
		setupRuntimeEventListeners: function(context) {
			console.log('setting up runtime events');
			if (window.mode === 'WEBAPP') {
				return;
			}
			browser.runtime.onMessage.addListener((request, sender, sendResponse) => {
				console.log(request);
				let type = request.type || '';
				if (type === 'app:drop') {
					context.dispatch('note/handleDrop', request.payload);
				}
				if (type === 'settings:updated') {
					context.dispatch('settings/update', { sender, request });
				}
				if (type === 'note:updated') {
					context.dispatch('note/updateNotes');
				}
				if (type === 'user:login') {
					context.dispatch('user/init').then(isLoggedIn => {
						if (isLoggedIn) {
							context.dispatch('afterLogin');
						}
					});
				}
				if (type === 'user:logout') {
					context.dispatch('note/reset');
					context.dispatch('bucket/reset');
					context.dispatch('user/logout');
				}
				if (type === 'user:updated') {
					context.dispatch('user/updateUser');
				}
				if (type === 'action:bookmark') {
					context.dispatch('note/createBookmark');
				}

				if (type === 'bucket:updated') {
					context.dispatch('bucket/refreshBuckets');
				}
				if (type === 'bucket:added') {
					context.dispatch('bucket/refreshBuckets');
				}
				if (type === 'bucket:deleted') {
					context.dispatch('bucket/refreshBuckets');
				}

				if (type === 'folder:updated') {
					context.dispatch('folder/refreshFolders');
				}
				if (type === 'folder:added') {
					context.dispatch('folder/refreshFolders');
				}
				if (type === 'folder:deleted') {
					context.dispatch('folder/refreshFolders');
				}

				if (type === 'error:network') {
					context.dispatch('message/onConnectionWarning');
				}

				if (type === 'action:dashboard:open') {
					context.dispatch('ui/openDashboard');
				}
			});
		}
	},
	modules: {
		user,
		note,
		bucket,
		folder,
		settings,
		cache,
		ui,
		realtimeio,
		message,
		dashboard,
		smartCopy,
		event
	}
});

export default store;
